import React, { useCallback, useEffect, useState, useId } from 'react';
import ProfileIcon from '../../assets/icons/profile.svg';
import logoutIcon from '../../assets/icons/logout.svg';
import { clearSession, markLogoutUnconfirmed } from 'config/session';
import axiosInstance, { logoutSession } from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';
import { buttonLike } from 'reusable/buttonLike';
import { useModalDialog } from 'reusable/useModalDialog';
import FetchFailed from 'reusable/FetchFailed';
import { useLatestRequest } from 'reusable/useLatestRequest';

interface ProfileType {
  name?: string;
  email?: string;
  role?: string;
  company_info?: {
    name?: string
  };
}

const Profile: React.FC = () => {
  const { t } = useTranslation('layout');
  const [showPopup, setShowPopup] = useState(false);
  const [profile, setProfile] = useState<ProfileType | null>(null);

  const handleLogout = async () => {
    // ADM-23 / ADM-17。Cookie の refresh をサーバーで失効させる（access は要らない）。
    // 失効に失敗しても手元のセッションは必ず破棄する。ただしサーバー側で
    // 失効できたかは未確認なので、成功と同じ扱いにせずログイン画面で伝える。
    const revoked = await logoutSession();
    if (!revoked) markLogoutUnconfirmed();
    clearSession();
    localStorage.removeItem('language');
    window.location.href = '/login';
  };

  const togglePopup = () => setShowPopup(!showPopup);
  // ADM-16。ログアウトの確認。Escape で取り消し、フォーカスを中に留める。
  // RV-125。ダイアログの名前の id は文書の中で一意にする。
  const titleId = useId();
  const dialogRef = useModalDialog(showPopup, () => setShowPopup(false));

  const confirmLogout = () => {
    togglePopup();
    handleLogout();
  };

  // EV-318。読み込みの失敗（名前・役割・会社が空のままにしない）と、最後の要求の応答だけを反映する番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    setFetchError(null);
    try {
      const apiUrl = '/v1/profile/';
      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      setProfile(response.data);
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Failed to fetch profile data', error);
      setFetchError(error);
    }
  }, [beginRequest]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  return (
    <div>
      <div
        className="flex flex-row justify-between bg-slate-200 bg-opacity-20 p-2 rounded-md hover:opacity-75"
        {...buttonLike(togglePopup)}
      >
        <div className="flex flex-row space-x-2 items-center">
          <img src={ProfileIcon} alt="" className="h-16" />
          <div className="">
            <p
              className="text-body-2 text-primary/50 truncate w-40"
              style={{ maxWidth: '8rem' }}
            >
              {profile ? profile.name || t('profile.UNKNOWN') : ''}
            </p>
            <p className="text-caption text-primary/50">{profile?.role ? t(`profile.${profile.role}`, { defaultValue: profile.role }) : ''}</p>
            <p className="text-caption text-primary/75">{profile?.company_info?.name}</p>
          </div>
        </div>
        <img src={logoutIcon} alt={t('logout.logout')} style={{ cursor: 'pointer' }} />
      </div>
      {/* EV-318。読めなかったことを出す。ログアウトのボタン（上の枠）の外に置く。 */}
      {fetchError ? (
        <div className="mt-2 rounded-md bg-white">
          <FetchFailed
            error={fetchError}
            onRetry={fetchData}
            message={t('ui.detail-fetch-failed', { ns: 'common' })}
            compact
          />
        </div>
      ) : null}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-75 z-50">
          <div
            className="bg-white py-6 rounded-lg shadow-lg space-y-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={dialogRef}
            tabIndex={-1}
          >
            <div className="px-24">
              <h2 id={titleId} className="text-title mb-4">{t('logout.title')}</h2>
            </div>
            <div className="flex flex-col border-y items-center justify-center py-4">
              <p className="px-6">{t('logout.info')}</p>
              <p className="px-6">{t('logout.info2')}</p>
            </div>
            <div className="flex justify-between space-x-4 px-6">
              <button type="button"
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-500 rounded hover:bg-gray-100"
                onClick={togglePopup}
              >
                {t('logout.cancel')}
              </button>
              <button type="button"
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={confirmLogout}
              >
                {t('logout.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
